import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ConnectorType, Station } from '@app/services/station.interface';
import { StationService } from '@app/services/station.service';
import { CONNECTOR_ICONS } from '@app/shared/utils/consts';

interface ConnectorFormData {
  id?: number;
  connectorTypeId: number;
  chargeKwh: number;
  chargeVolts: number;
  chargeAmps: number;
  powerType: string;
  isNew?: boolean;
  isDeleted?: boolean;
}

interface ChargerFormData {
  id?: number;
  name: string;
  description: string;
  isPaid: boolean;
  connectors: ConnectorFormData[];
  isNew?: boolean;
  isDeleted?: boolean;
}

@Component({
  selector: 'app-edit-charger-connectors-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDividerModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './edit-charger-connectors-dialog.component.html',
  styleUrl: './edit-charger-connectors-dialog.component.scss'
})
export class EditChargerConnectorsDialogComponent implements OnInit {
  dialogRef = inject(MatDialogRef<EditChargerConnectorsDialogComponent>);
  data: { station: Station } = inject(MAT_DIALOG_DATA);

  state = signal<'initial' | 'loading' | 'success' | 'error'>('initial');
  connectorTypes: ConnectorType[] = [];
  chargers: ChargerFormData[] = [];
  powerTypes = ['AC', 'DC'];

  constructor(
    private stationService: StationService,
  ) {}

  ngOnInit(): void {
    // Load connector types
    this.stationService.getConnectorTypes().subscribe(types => {
      this.connectorTypes = types;
    });

    // Initialize chargers from station data
    this.chargers = (this.data.station?.chargers || []).map(charger => ({
      id: charger.id,
      name: charger.name || '',
      description: charger.description || '',
      isPaid: charger.isPaid || false,
      connectors: (charger.connectors || []).map(connector => ({
        id: connector.id,
        connectorTypeId: connector.connectorTypeId,
        chargeKwh: connector.chargeKwh || 0,
        chargeVolts: connector.chargeVolts || 0,
        chargeAmps: connector.chargeAmps || 0,
        powerType: connector.powerType || 'AC',
      })),
    }));
  }

  close() {
    this.dialogRef.close();
  }

  addCharger() {
    this.chargers.push({
      name: '',
      description: '',
      isPaid: false,
      connectors: [],
      isNew: true,
    });
  }

  removeCharger(index: number) {
    const charger = this.chargers[index];
    if (charger.isNew) {
      this.chargers.splice(index, 1);
    } else {
      charger.isDeleted = true;
    }
  }

  addConnector(chargerIndex: number) {
    this.chargers[chargerIndex].connectors.push({
      connectorTypeId: this.connectorTypes[0]?.connectorTypeId || 1,
      chargeKwh: 0,
      chargeVolts: 0,
      chargeAmps: 0,
      powerType: 'AC',
      isNew: true,
    });
  }

  removeConnector(chargerIndex: number, connectorIndex: number) {
    const connector = this.chargers[chargerIndex].connectors[connectorIndex];
    if (connector.isNew) {
      this.chargers[chargerIndex].connectors.splice(connectorIndex, 1);
    } else {
      connector.isDeleted = true;
    }
  }

  getConnectorIcon(connectorTypeId: number): string {
    const connectorType = this.connectorTypes.find(c => c.connectorTypeId === connectorTypeId);
    const name = connectorType?.name?.replace(/[^a-zA-Z0-9]/g, '') || '';
    return CONNECTOR_ICONS[name] ?? CONNECTOR_ICONS[''];
  }

  getConnectorName(connectorTypeId: number): string {
    return this.connectorTypes.find(c => c.connectorTypeId === connectorTypeId)?.name || 'Unknown';
  }

  getVisibleChargers(): ChargerFormData[] {
    return this.chargers.filter(c => !c.isDeleted);
  }

  getVisibleConnectors(charger: ChargerFormData): ConnectorFormData[] {
    return charger.connectors.filter(c => !c.isDeleted);
  }

  async submitForm() {
    this.state.set('loading');
    const stationId = this.data.station.id;

    try {
      // Process chargers
      for (const charger of this.chargers) {
        if (charger.isDeleted && charger.id) {
          // Delete charger
          await this.stationService.deleteCharger(charger.id).toPromise();
        } else if (charger.isNew && !charger.isDeleted) {
          // Add new charger
          const result: any = await this.stationService.addCharger(stationId, {
            name: charger.name,
            description: charger.description,
            isPaid: charger.isPaid,
          }).toPromise();

          // Add connectors to new charger
          for (const connector of charger.connectors.filter(c => !c.isDeleted)) {
            await this.stationService.addConnector(result.id, {
              connectorTypeId: connector.connectorTypeId,
              chargeKwh: connector.chargeKwh,
              chargeVolts: connector.chargeVolts,
              chargeAmps: connector.chargeAmps,
              powerType: connector.powerType,
            }).toPromise();
          }
        } else if (charger.id && !charger.isDeleted) {
          // Update existing charger
          await this.stationService.updateCharger(charger.id, {
            name: charger.name,
            description: charger.description,
            isPaid: charger.isPaid,
          }).toPromise();

          // Process connectors
          for (const connector of charger.connectors) {
            if (connector.isDeleted && connector.id) {
              await this.stationService.deleteConnector(connector.id).toPromise();
            } else if (connector.isNew && !connector.isDeleted) {
              await this.stationService.addConnector(charger.id, {
                connectorTypeId: connector.connectorTypeId,
                chargeKwh: connector.chargeKwh,
                chargeVolts: connector.chargeVolts,
                chargeAmps: connector.chargeAmps,
                powerType: connector.powerType,
              }).toPromise();
            } else if (connector.id && !connector.isDeleted) {
              await this.stationService.updateConnector(connector.id, {
                connectorTypeId: connector.connectorTypeId,
                chargeKwh: connector.chargeKwh,
                chargeVolts: connector.chargeVolts,
                chargeAmps: connector.chargeAmps,
                powerType: connector.powerType,
              }).toPromise();
            }
          }
        }
      }

      this.state.set('success');
      setTimeout(() => {
        this.dialogRef.close({ updated: true, chargers: this.chargers });
      }, 1000);
    } catch (error) {
      this.state.set('error');
    }
  }
}
